import React, { useState } from 'react';
import { StabeonUser, ACTIVITY_CATEGORIES } from '../../types/user';
import { AvatarDisplay } from '../avatar/AvatarDisplay';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useGamification, BADGES } from '../../hooks/useGamification';
/* eslint-enable @typescript-eslint/no-unused-vars */
import { useFollows, useFollowerCount } from '../../hooks/useFollows';
import { QRProfileCard } from './QRProfileCard';
import { StabeonEvent } from '../../types/event';

interface Props {
  user: StabeonUser;
  myId?: string;
  allEvents?: StabeonEvent[];
  onChat: (user: StabeonUser) => void;
  onClose: () => void;
  onBlockReport?: (user: StabeonUser) => void;
  isMe?: boolean;
}

function timeAgo(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}


export function ProfilePanel({ user, myId, allEvents = [], onChat, onClose, onBlockReport, isMe = false }: Props) {
  const catInfo = ACTIVITY_CATEGORIES.find(c => c.key === user.activityCategory);
  const { isFollowing, follow, unfollow } = useFollows(myId || null);
  const followerCount = useFollowerCount(user.nullifierHash);
  const [showQR, setShowQR] = useState(false);

  // Compute organizer stats from passed events (no direct GUN scan)
  const myEvents = allEvents.filter(e => e.creatorId === user.nullifierHash);
  const orgStats = {
    eventCount: myEvents.length,
    totalAttendees: myEvents.reduce((sum, e) => sum + (e.attendeeCount || 0), 0),
    avgRating: 0,
    ratingCount: 0,
  };

  if (showQR && isMe) {
    return <QRProfileCard userId={user.nullifierHash} nickname={user.nickname} onClose={() => setShowQR(false)} />;
  }

  const following = isFollowing(user.nullifierHash);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        {user.avatar && (
          <AvatarDisplay avatar={user.avatar} size={60} isMoving={user.isMoving} isMe={isMe} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-stabeon-text font-semibold text-lg truncate">{user.nickname}</h3>
            {/* World ID verified human */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 text-stabeon-accent" aria-label="Verified Human">
              <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15"/>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                user.isOnline ? 'bg-stabeon-online' : 'bg-stabeon-muted'
              }`}
            />
          </div>
          <p className="text-stabeon-muted text-xs mt-0.5">
            {user.isOnline ? 'Online' : `Last seen ${timeAgo(user.lastSeen)}`}
          </p>
          {followerCount > 0 && (
            <p className="text-stabeon-muted text-[11px] mt-0.5">
              <span className="font-semibold text-stabeon-text">{followerCount}</span> {followerCount === 1 ? 'follower' : 'followers'}
            </p>
          )}
        </div>
        {isMe && (
          <button
            onClick={() => setShowQR(true)}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-stabeon-muted hover:text-stabeon-accent hover:bg-stabeon-accent/10 transition-all"
            title="My QR card"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <path d="M14 14h3v3m0 4v-4m4 0h-4m4-3v3"/>
            </svg>
          </button>
        )}
      </div>

      {/* Activity */}
      {(user.activity || user.activityCategory) && (
        <div className="card px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">{catInfo?.emoji || '✨'}</span>
            <div>
              <p className="text-stabeon-text text-sm font-medium">
                {catInfo?.label || 'Something'}
              </p>
              {user.activity && (
                <p className="text-stabeon-muted text-xs mt-0.5">{user.activity}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Organizer reputation */}
      {orgStats.eventCount > 0 && (
        <div className="card px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">🎪</span>
            <div>
              <p className="text-stabeon-text text-sm font-medium">
                Organizer
                {orgStats.eventCount >= 3 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-stabeon-accent/20 text-stabeon-accent text-[10px] font-bold">Verified</span>
                )}
              </p>
              <p className="text-stabeon-muted text-xs">
                {orgStats.eventCount} event{orgStats.eventCount !== 1 ? 's' : ''} · {orgStats.totalAttendees} total attendees
              </p>
            </div>
          </div>
          {orgStats.ratingCount > 0 && (
            <div className="flex items-center gap-1.5">
              {[1,2,3,4,5].map(s => (
                <span key={s} className={`text-sm ${s <= Math.round(orgStats.avgRating) ? 'text-yellow-400' : 'text-stabeon-border'}`}>★</span>
              ))}
              <span className="text-stabeon-muted text-xs ml-1">{orgStats.avgRating} ({orgStats.ratingCount} rating{orgStats.ratingCount !== 1 ? 's' : ''})</span>
            </div>
          )}
        </div>
      )}

      {/* Gamification */}
      <div className="card px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">🎮</span>
          <div>
            <p className="text-stabeon-text text-sm font-medium">Gamification</p>
            <p className="text-stabeon-muted text-xs">Level {user?.level || 1} · {user?.streak || 0} day streak</p>
          </div>
        </div>
        <div className="w-full bg-stabeon-border rounded-full h-2.5 overflow-hidden">
          <div
            className="h-2.5 rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(100, ((user?.experience || 0) % 100))}%`,
              background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
            }}
          />
        </div>
        <p className="text-stabeon-muted text-xs mt-1">{(user?.experience || 0) % 100}/100 XP to next level</p>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          {user?.badges?.map(badgeId => {
            const badge = BADGES.find(b => b.id === badgeId);
            if (!badge) return null;
            return (
              <div key={badgeId} className="flex items-center gap-1">
                <div className="h-6 w-6 rounded-full flex items-center justify-center bg-stabeon-accent/20 text-stabeon-accent text-xs">
                  {badge.icon}
                </div>
                <span className="text-[9px] font-medium text-stabeon-muted">{badge.name}</span>
              </div>
            );
          }) || []}
        </div>
      </div>

      {/* Movement */}
      {user.isMoving && (
        <div className="flex items-center gap-2 text-stabeon-moving text-sm">
          <span>🚶</span>
          <span>Currently moving</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button onClick={onClose} className="btn-ghost flex-1">
          Close
        </button>
        {!isMe && myId && (
          <button
            onClick={() => following ? unfollow(user.nullifierHash) : follow(user.nullifierHash)}
            className={`flex-1 text-sm py-2.5 rounded-xl font-semibold transition-all ${
              following ? 'pill-active' : 'pill-inactive'
            }`}
          >
            {following ? '✓ Following' : '+ Follow'}
          </button>
        )}
        {!isMe && myId && (
          <button
            onClick={() => { onChat(user); onClose(); }}
            className="btn-primary flex-1"
          >
            💬 Message
          </button>
        )}
      </div>
      {!isMe && !myId && (
        <p className="text-stabeon-muted text-xs text-center py-1">
          Sign in to follow and message people
        </p>
      )}
      {!isMe && myId && onBlockReport && (
        <button
          onClick={() => onBlockReport(user)}
          className="text-stabeon-muted text-xs text-center hover:text-stabeon-danger transition-colors py-1"
        >
          Block or report
        </button>
      )}
    </div>
  );
}
